import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "./Star";
import { Carousel } from "./Carousel";
import { useI18n } from "@/context/LanguageContext";

const Media = ({ item }) => {
  if (item.gallery && item.gallery.length > 1) {
    return <Carousel images={item.gallery} alt={item.title} />;
  }
  return (
    <div className="space-y-4">
      <div className="img-card relative aspect-[4/5] bg-[#F7E9DD] overflow-hidden border-2 border-[#141518] shadow-hard-lg">
        <img src={item.img} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      {item.video && (
        <div className="relative aspect-[4/5] overflow-hidden bg-[#141518] border-2 border-[#141518] shadow-hard-lg">
          <video
            src={item.video}
            data-testid={`story-video-${item.eyebrow}`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
};

export const Story = () => {
  const { content, t } = useI18n();
  const story = content.story;
  const [active, setActive] = useState(0);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observers = [];
    sectionRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActive(i);
        },
        { threshold: 0.55, rootMargin: "-20% 0px -20% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const goTo = (i) => {
    const el = sectionRefs.current[i];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <section id="historia" data-testid="story-section" className="bg-[#FFF7F1] py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-10 md:mb-16">
          <div className="flex items-center gap-3">
            <Star size={14} className="text-[#F0311B]" />
            <span className="font-condensed font-bold uppercase tracking-[0.25em] text-sm md:text-base">{t.story.eyebrow}</span>
          </div>
          <h2 className="mt-4 font-display font-bold text-[14vw] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] text-[#141518]">
            <span className="italic text-[#F0311B]">{t.story.titlePre}</span> {t.story.titleMid} <br />
            {t.story.titleEnd}
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 lg:gap-16">
          <aside className="hidden md:block md:col-span-2">
            <div data-testid="story-tracklist" className="sticky top-28 border-t-2 border-[#141518]">
              <div className="py-3 font-condensed font-bold uppercase tracking-[0.25em] text-[10px] text-[#F0311B] border-b border-[#141518]/15">
                {t.story.tracklist}
              </div>
              <ol className="divide-y divide-[#141518]/15">
                {story.map((it, i) => {
                  const isActive = i === active;
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        data-testid={`story-track-${i}`}
                        onClick={() => goTo(i)}
                        className={`group w-full text-left py-4 grid grid-cols-[36px_1fr_20px] items-center gap-3 transition-colors ${isActive ? "" : "hover:bg-[#141518]/5"}`}
                      >
                        <span className={`font-display font-bold text-xl lg:text-2xl tabular-nums leading-none ${isActive ? "text-[#F0311B]" : "text-[#141518]/30"}`}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={`font-condensed font-bold uppercase tracking-[0.22em] text-[11px] transition-colors ${isActive ? "text-[#141518]" : "text-[#141518]/40"}`}>
                          {t.story.capPrefix} {String(i + 1).padStart(2, "0")}
                        </span>
                        <span aria-hidden className={`block h-[2px] transition-all justify-self-end ${isActive ? "bg-[#F0311B] w-5" : "bg-transparent w-0"}`} />
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>

          <div className="col-span-12 md:col-span-10 space-y-20 md:space-y-32">
            {story.map((it, i) => (
              <motion.article
                key={i}
                ref={(el) => (sectionRefs.current[i] = el)}
                data-testid={`story-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-12 gap-5 md:gap-10 items-center"
              >
                <div className="col-span-12 md:col-span-7 order-2 md:order-1">
                  <Media item={it} />
                </div>
                <div className="col-span-12 md:col-span-5 order-1 md:order-2">
                  <div className="flex items-center gap-3">
                    <span className="font-condensed font-bold uppercase tracking-[0.25em] text-sm text-[#F0311B]">{it.eyebrow}</span>
                    <span className="flex-1 h-px bg-[#141518]/30" />
                  </div>
                  <h3 data-testid={`story-card-${i}-title`} className="mt-3 font-display font-bold text-[9vw] sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1] text-[#141518]">
                    {it.title}
                  </h3>
                  {it.text &&
                    (it.highlight ? (
                      <p className="mt-5 font-display font-bold italic text-[10vw] sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1] text-[#F0311B]">
                        {it.text}
                      </p>
                    ) : (
                      <p className="mt-4 md:mt-5 font-display font-bold italic text-2xl md:text-3xl lg:text-4xl leading-[1.05] text-[#141518]">
                        {it.text}
                      </p>
                    ))}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
